import { logger } from '@/lib/logger';
import type { ApiResponse } from '@asetquhub/validation';
import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { auth } from './firebase';
import { ApiErrorResponse } from '../types/error-type';

declare module 'axios' {
  interface AxiosRequestConfig {
    _skipSignOut?: boolean;
    _retryCount?: number;
  }
}

const baseUrl: string = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Backend switching: reviewer mode routes all API calls to staging
export const REVIEWER_API_URL_KEY = 'reviewer_api_url';

export const getActiveBaseUrl = (): string => {
  try {
    const reviewerUrl = localStorage.getItem(REVIEWER_API_URL_KEY);
    if (reviewerUrl) return reviewerUrl;
  } catch {
    // localStorage unavailable (SSR/private browsing)
  }
  return baseUrl;
};

export const axiosInstance = axios.create({
  baseURL: baseUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: Add Firebase ID token to Authorization header + backend switching
axiosInstance.interceptors.request.use(
  async config => {
    // Backend switching: use staging URL when reviewer mode is active
    config.baseURL = getActiveBaseUrl();

    try {
      const user = auth.currentUser;
      if (user) {
        const idToken = await user.getIdToken();
        config.headers.Authorization = `Bearer ${idToken}`;
      }
    } catch (error) {
      logger.error('Error getting Firebase ID token:', error);
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle errors
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    // Auto-retry on 429: prefer Retry-After header, fall back to parsing message body.
    // Cap at 3 retries to avoid infinite loops on persistent rate limits.
    if (error.response?.status === 429 && error.config) {
      const retryCount = error.config._retryCount ?? 0;
      if (retryCount < 3) {
        const retryAfterHeader = error.response.headers?.['retry-after'];
        let delaySecs = retryAfterHeader ? parseInt(retryAfterHeader) : 0;
        if (!delaySecs || isNaN(delaySecs)) {
          const responseData = error.response.data as ApiResponse<unknown> | undefined;
          const msg = responseData?.errors?.[0]?.message ?? '';
          const match = msg.match(/(\d+)\s+second/);
          delaySecs = match ? parseInt(match[1]) : 2;
        }
        await new Promise(resolve => setTimeout(resolve, delaySecs * 1000 + 300));
        error.config._retryCount = retryCount + 1;
        return axiosInstance(error.config);
      }
    }

    if (error.response?.status === 401) {
      // Only auto-sign-out if we actually tried to authenticate (had Authorization header)
      // and the caller has not opted out (e.g. wallet-pay endpoints handle auth separately).
      const hadAuthHeader = error.config?.headers?.Authorization;
      const skipSignOut = error.config?._skipSignOut;

      if (hadAuthHeader && !skipSignOut) {
        try {
          await auth.signOut();
        } catch (signOutError) {
          logger.error('Error signing out:', signOutError);
        }
      }

      const authError = new ApiErrorResponse(
        [
          {
            message: 'Session expired. Please log in again.',
            code: 'UNAUTHORIZED',
            type: 'authentication',
          },
        ],
        401,
        'Unauthorized'
      );
      return Promise.reject(authError);
    }

    if (error.response?.data) {
      const responseData = error.response.data as ApiResponse<unknown>;

      if (responseData.errors && responseData.errors.length > 0) {
        const apiError = new ApiErrorResponse(
          responseData.errors,
          error.response.status,
          error.response.statusText,
          responseData.requestId,
        );
        return Promise.reject(apiError);
      }
    }

    if (error.code === 'NETWORK_ERROR' || !error.response) {
      const networkError = new ApiErrorResponse(
        [
          {
            message: 'Network error. Please check your connection.',
            code: 'NETWORK_ERROR',
            type: 'system',
          },
        ],
        0,
        'Network Error'
      );
      return Promise.reject(networkError);
    }

    const responseData = error.response?.data as { message?: string } | undefined;
    const httpError = new ApiErrorResponse(
      [
        {
          message: responseData?.message || error.message || 'An unexpected error occurred',
          code: `HTTP_${error.response?.status}`,
          type: 'system',
        },
      ],
      error.response?.status || 500,
      error.response?.statusText || 'Internal Server Error'
    );

    return Promise.reject(httpError);
  }
);

export const api = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return axiosInstance.get(url, config);
  },

  // POST request
  post: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> => {
    return axiosInstance.post(url, data, config);
  },

  // PUT request
  put: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> => {
    return axiosInstance.put(url, data, config);
  },

  // PATCH request
  patch: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> => {
    return axiosInstance.patch(url, data, config);
  },

  // DELETE request
  delete: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return axiosInstance.delete(url, config);
  },
};

export default axiosInstance;
